/**
 * Key Generation Utilities for OCPP Server Management
 *
 * Generates cryptographically secure API keys and webhook secrets
 * client-side for one-time display before secure storage
 */

/**
 * Generate a cryptographically secure random string
 * @param length - Length of the string to generate
 * @returns Base64url encoded random string
 */
function generateSecureRandomString(length: number): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  // Convert to base64url (URL-safe base64)
  return btoa(String.fromCharCode.apply(null, Array.from(array)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Generate a secure API key for OCPP server to authenticate to backend
 * @param prefix - Optional prefix for the key (e.g., 'ocpp_key')
 * @returns Generated OCPP to Backend API key
 */
export function generateOcppToBackendApiKey(prefix: string = 'ocpp_key'): string {
  // Generate 32 bytes of random data
  const randomPart = generateSecureRandomString(32);
  return `${prefix}_${randomPart}`;
}

/**
 * Generate a secure API key for backend to authenticate to OCPP server
 * @param prefix - Optional prefix for the key (e.g., 'backend_key')
 * @returns Generated Backend to OCPP API key
 */
export function generateBackendToOcppApiKey(prefix: string = 'backend_key'): string {
  // Generate 32 bytes of random data
  const randomPart = generateSecureRandomString(32);
  return `${prefix}_${randomPart}`;
}

/**
 * Generate both API keys for OCPP server authentication
 * @returns Object containing both directional API keys
 */
export function generateOcppServerKeys(): {
  ocppToBackendApiKey: string;
  backendToOcppApiKey: string;
  apiKeySuffix: string;
} {
  const ocppToBackendApiKey = generateOcppToBackendApiKey();
  const backendToOcppApiKey = generateBackendToOcppApiKey();

  // Extract last 4 characters for display
  const apiKeySuffix = ocppToBackendApiKey.slice(-4);

  return {
    ocppToBackendApiKey,
    backendToOcppApiKey,
    apiKeySuffix,
  };
}

/**
 * Format key for display with copy functionality
 * @param key - The key to format
 * @returns Formatted display string
 */
export function formatKeyForDisplay(key: string): string {
  if (!key) return '';

  // Show first 8 and last 4 characters
  if (key.length > 20) {
    const prefix = key.substring(0, 8);
    const suffix = key.substring(key.length - 4);
    return `${prefix}...${suffix}`;
  }

  return key;
}

/**
 * Copy key to clipboard
 * @param key - The key to copy
 * @returns Promise that resolves when copied
 */
export async function copyToClipboard(key: string): Promise<void> {
  if (!navigator.clipboard) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = key;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
    } finally {
      document.body.removeChild(textArea);
    }
    return;
  }

  await navigator.clipboard.writeText(key);
}

/**
 * Validate OCPP to Backend API key format
 * @param ocppToBackendApiKey - The API key to validate
 * @returns True if valid format
 */
export function validateOcppToBackendApiKey(ocppToBackendApiKey: string): boolean {
  // Check format: prefix_base64urlstring
  const pattern = /^ocpp_key_[A-Za-z0-9_-]{40,}$/;
  return pattern.test(ocppToBackendApiKey);
}

/**
 * Validate Backend to OCPP API key format
 * @param backendToOcppApiKey - The API key to validate
 * @returns True if valid format
 */
export function validateBackendToOcppApiKey(backendToOcppApiKey: string): boolean {
  // Check format: prefix_base64urlstring
  const pattern = /^backend_key_[A-Za-z0-9_-]{40,}$/;
  return pattern.test(backendToOcppApiKey);
}